import React, { useEffect, useRef, useState } from "react";
import { X, CreditCard, CheckCircle2, ExternalLink, Copy, RefreshCw } from "lucide-react";
import { portalApi, rupiah, errMsg } from "./portalApi";

const CHANNELS = [
  ["QRIS", "QRIS (semua e-wallet & m-banking)"],
  ["BRIVA", "Virtual Account BRI"],
  ["BCAVA", "Virtual Account BCA"],
  ["BNIVA", "Virtual Account BNI"],
  ["MANDIRIVA", "Virtual Account Mandiri"],
  ["PERMATAVA", "Virtual Account Permata"],
  ["OVO", "OVO"],
  ["DANA", "DANA"],
  ["SHOPEEPAY", "ShopeePay"],
];

export const PortalPayModal = ({ invoice, onClose, onPaid }) => {
  const [channel, setChannel] = useState("QRIS");
  const [loading, setLoading] = useState(false);
  const [checkout, setCheckout] = useState(null);
  const [paid, setPaid] = useState(false);
  const [error, setError] = useState("");
  const pollRef = useRef(null);

  useEffect(() => () => pollRef.current && clearInterval(pollRef.current), []);

  const checkStatus = async (trxId) => {
    const res = await portalApi.get(`/payments/status/${trxId}`);
    if (res.data.paid) {
      clearInterval(pollRef.current);
      setPaid(true);
      setTimeout(() => { onPaid?.(); onClose(); }, 1800);
    }
    return res.data.paid;
  };

  const charge = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await portalApi.post(`/tagihan/${invoice.id}/bayar`, { channel });
      setCheckout(res.data);
      pollRef.current = setInterval(() => checkStatus(res.data.trx_id).catch(() => {}), 4000);
      if (res.data.checkout_url && !res.data.qr_url && !res.data.pay_code) window.open(res.data.checkout_url, "_blank");
    } catch (e) {
      setError(errMsg(e, "Gagal membuat pembayaran"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid="portal-pay-modal">
      <div className="bg-[#0f172a] border border-slate-700 rounded-t-2xl sm:rounded-2xl w-full max-w-md shadow-2xl max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-800">
          <div className="flex items-center gap-2">
            <CreditCard className="w-4 h-4 text-emerald-400" />
            <div>
              <h3 className="text-sm font-bold text-white">Bayar Online</h3>
              <p className="text-[11px] text-slate-400 font-mono">{invoice.no_invoice}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white p-1" data-testid="portal-pay-close"><X className="w-4 h-4" /></button>
        </div>

        <div className="p-5 space-y-4">
          <div className="p-3 rounded-xl bg-slate-900 border border-slate-800 flex items-center justify-between">
            <span className="text-xs text-slate-400">Total dibayar</span>
            <span className="text-lg font-bold text-emerald-400 font-mono" data-testid="portal-pay-amount">{rupiah(invoice.sisa_tagihan)}</span>
          </div>

          {error && <div className="p-2.5 rounded-lg bg-rose-500/10 border border-rose-500/40 text-rose-300 text-xs" data-testid="portal-pay-error">{error}</div>}

          {paid ? (
            <div className="text-center py-6 space-y-2" data-testid="portal-pay-success">
              <CheckCircle2 className="w-12 h-12 text-emerald-400 mx-auto" />
              <p className="text-white font-bold">Pembayaran diterima!</p>
              <p className="text-xs text-slate-400">Layanan Anda aktif. Terima kasih.</p>
            </div>
          ) : !checkout ? (
            <>
              <div>
                <label className="block text-xs text-slate-300 mb-1">Pilih metode</label>
                <select value={channel} onChange={(e) => setChannel(e.target.value)} className="w-full px-3 py-2.5 bg-slate-950 border border-slate-700 rounded-xl text-sm text-white" data-testid="portal-pay-channel">
                  {CHANNELS.map(([c, l]) => <option key={c} value={c}>{l}</option>)}
                </select>
              </div>
              <button onClick={charge} disabled={loading} className="w-full py-2.5 rounded-xl bg-gradient-to-r from-emerald-500 to-cyan-600 text-white font-semibold text-sm disabled:opacity-60" data-testid="portal-pay-submit">
                {loading ? "Membuat transaksi..." : "Lanjutkan Pembayaran"}
              </button>
            </>
          ) : (
            <div className="space-y-3" data-testid="portal-pay-instructions">
              {checkout.qr_url && (
                <div className="bg-white rounded-xl p-3 flex justify-center"><img src={checkout.qr_url} alt="QRIS" className="w-56 h-56 object-contain" /></div>
              )}
              {checkout.pay_code && (
                <div className="p-3 rounded-xl bg-slate-900 border border-slate-800">
                  <p className="text-[11px] text-slate-400 mb-1">Nomor Virtual Account / Kode Bayar</p>
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-mono text-lg text-white tracking-wider">{checkout.pay_code}</span>
                    <button onClick={() => navigator.clipboard?.writeText(checkout.pay_code)} className="p-1.5 text-slate-400 hover:text-white" title="Salin"><Copy className="w-4 h-4" /></button>
                  </div>
                </div>
              )}
              {checkout.checkout_url && (
                <a href={checkout.checkout_url} target="_blank" rel="noreferrer" className="flex items-center justify-center gap-2 w-full py-2.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-white text-sm font-semibold">
                  Buka Halaman Pembayaran <ExternalLink className="w-4 h-4" />
                </a>
              )}
              <div className="flex items-center justify-between text-[11px] text-slate-400">
                <span className="flex items-center gap-1"><RefreshCw className="w-3 h-3 animate-spin" /> Menunggu pembayaran...</span>
                <button onClick={() => checkStatus(checkout.trx_id).then((p) => !p && setError("Belum terdeteksi, selesaikan pembayaran lalu cek lagi")).catch(() => {})} className="text-cyan-400 hover:underline" data-testid="portal-pay-check">Cek status</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
